import { ICameraChannel } from "@app/main/components/videos/models/ICameraChannel";
import { CameraModule } from "@app/main/modules/cameras/camera.module.ajs";
import { DateService } from "@app/services/date.service";
import { IFilterModel } from "@directives/_resourceFilters/IFilterModel";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { DateRange } from "@enums/DateRange.enum";
import { SortType } from "@enums/SortType";
import { FoundationPeripheralService } from "@foundation/_services/peripheral.service.ajs";
import { IDriverFilter } from "@interfaces/filters/IDriverFilter";
import { IVehicleFilter } from "@interfaces/filters/IVehicleFilter";
import { IJourney } from "@interfaces/IJourney";
import { IVehicle } from "@interfaces/IVehicle";
import { ConstantsService } from "@services/constantsService";
import { DriverAssignmentService } from "@services/driverAssignment.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { StateService } from "@uirouter/angularjs";
import * as angular from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment";
import { CameraDisplayService } from "../cameraDisplay.service";
import { IVideoEntry } from "../models/IVideoEntry";
import { CameraService } from "../services/camera.service";
import { VideoEntryService } from "../services/video-entry.service";
import { VideoEventCodeService } from "../services/video-event-code.service";
import "./vehicleVideosPage.scss";


interface IComponentBindings {
}

class ComponentController implements angular.IComponentController, IComponentBindings {

    loading = false;

    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow<IVehicle>[] = [];

    journeys: IJourney[] = [];

    // raw data
    vehicles: IVehicle[];
    videoEntries: IVideoEntry[];
    cameraChannels: ICameraChannel[];

    vehiclesToDisplay: IVehicle[];
    selectedVehicle: IVehicle;

    isMobile: boolean;

    loadingVideos = false;

    filterModel: IFilterModel;

    vehicleFilter: IVehicleFilter;
    driverFilter: IDriverFilter;

    defaultDateRange = DateRange.Latest7Days;

    searchString: string;

    latestOfflineStatusTime: Date;

    getDataInterval: angular.IPromise<any>;
    gettingData: boolean;

    static $inject = ["$q", "$scope", "$interval", "driverAssignmentService", "constantsService", "cameraDisplayService", "dateService", "veriDate", "veriTableService", "foundationPeripheralService", "translationService", "videoEventCodeService", "cameraService", "videoEntryService", "$injector"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $scope: angular.IScope,
        private readonly $interval: angular.IIntervalService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly constantsService: ConstantsService,
        private readonly cameraDisplayService: CameraDisplayService,
        private readonly dateService: DateService,
        private readonly veriDate: VeriDate,
        private readonly veriTableService: VeriTableService,
        private readonly foundationPeripheralService: FoundationPeripheralService,
        private readonly translationService: TranslationService,
        private readonly videoEventCodeService: VideoEventCodeService,
        private readonly cameraService: CameraService,
        private readonly videoEntryService: VideoEntryService,
        private readonly $injector: angular.auto.IInjectorService) {
    }

    $onInit(): void {
        this.isMobile = this.constantsService.getIsMobile();
        this.createTableHeaders();

        this.getData();

        this.getDataInterval = this.$interval(() => {
            this.getData(true);
        }, 60_000);
    }

    $onDestroy() {
        if (this.getDataInterval) {
            this.$interval.cancel(this.getDataInterval);
        }
    }

    vehicleClicked(vehicle: IVehicle) {

        if (vehicle == this.selectedVehicle) {
            this.vehicleClicked(null);
            return;
        }

        this.showVehicleDialog(vehicle);
    }

    playVideo(video: IVideoEntry, $event: MouseEvent) {
        this.cameraDisplayService.showVideoPlayer(video, $event);
    }

    vehicleFilterChanged(filter: IVehicleFilter) {
        this.vehicleFilter = filter;
        this.applyFilters();
        this.createTableRows();
    }

    applyFilters() {
        var vehicles = copy(this.vehicles)

        if (this.vehicleFilter && this.vehicleFilter.vehicle) {
            vehicles = vehicles.filter(v => v.Id == this.vehicleFilter.vehicle.Id);
        }

        if (this.driverFilter && this.driverFilter.driver) {
            vehicles = vehicles.filter(v => v.Driver?.Id == this.driverFilter.driver.Id);
        }

        this.vehiclesToDisplay = vehicles;
    }

    driverFilterChanged(filter: IDriverFilter) {
        this.driverFilter = filter;
        this.applyFilters();
        this.createTableRows();
    }

    onSearch(searchString) {
        this.searchString = searchString;
        this.$scope.$applyAsync();
    }

    // ---

    private getData(silentLoad: boolean = false): angular.IPromise<void> {

        if (this.gettingData) {
            return this.$q.resolve();
        }

        this.gettingData = true;

        const vehiclePromise = this.driverAssignmentService.getVehiclesWithDrivers({ includeLatestPlots: true, includePeripheralMappings: true });
        const videoEntriesDateRange = this.dateService.dateRangeFromEnum(this.defaultDateRange);
        const videoEntriesPromise = this.videoEntryService.getAllVideosEntries(videoEntriesDateRange.start, videoEntriesDateRange.end) as angular.IPromise<IVideoEntry[]>;
        const channelsPromise = this.cameraService.getCameraChannels() as angular.IPromise<ICameraChannel[]>;

        this.loading = !silentLoad;

        this.$q.all([vehiclePromise, videoEntriesPromise, channelsPromise]).then(([vehicles, videoEntries, cameraChannels]) => {

            this.cameraChannels = cameraChannels;
            this.videoEntries = videoEntries;

            return this.foundationPeripheralService.getPeripheralsAndMapToVehicles(vehicles).then(() => {
                this.vehicles = vehicles.filter(v => this.foundationPeripheralService.isAnyPeripheralCamera(v.peripherals));
            });

        }).finally(() => {

            this.processVehicles();
            this.prepareTable();

            this.loading = false;
            this.gettingData = false;
        });
    }

    private processVehicles(): void {
        this.vehicles = this.cameraService.getVehiclesWithMetadataUpdated(this.vehicles, this.videoEntries);
        this.vehicles = this.cameraService.getVehiclesWithStreamingChannelsMapped(this.cameraChannels, this.vehicles);
    }

    private prepareTable(): void {
        this.applyFilters();
        this.createTableRows();
    }

    private showVehicleDialog(vehicle: IVehicle) {

        var $state = this.$injector.get("$state") as StateService;
        $state.go(CameraModule.VehicleView, { vehicleId: vehicle.Id });
    }

    private createTableHeaders() {
        this.tableHeaders = [
            { title: this.translationService.translate("VehicleVideosPage.Availability"), width: 125, sortable: true, sortType: SortType.Date },
            this.veriTableService.createVehicleRegHeader(),
            { ...this.veriTableService.createVehicleRegHeader({ title: this.translationService.t("Common.Trailer"), hideIfNoData: true }), hideIfNoData: true },
            { title: this.translationService.translate("Common.Driver"), sortable: true, hideOnMobile: true },
            { title: this.translationService.translate("Common.Location"), sortable: false },
            { title: this.translationService.translate("CameraManagement.ActiveChannels"), width: 135, sortable: true, sortType: SortType.Number, hideOnMobile: true },
            { title: this.translationService.translate("cameraService.DmsEvents"), width: 110, sortable: true, sortType: SortType.Number },
            { title: this.translationService.translate("cameraService.AdasEvents"), width: 110, sortable: true, sortType: SortType.Number }
        ];
    }

    private createTableRows() {
        this.updateLatestOfflineStatusTime(this.vehiclesToDisplay);

        this.tableRows = this.veriTableService.createTableRowsWithCells(this.vehiclesToDisplay, data => this.createTableRowCells(data));
    }

    private createTableRowCells(vehicle: IVehicle): IVeriTableRowCell[] {
        const cells: IVeriTableRowCell[] = [];

        cells.push(this.veriTableService.createVehicleOnlineStatusCell(vehicle, this.veriDate.createMoment(this.latestOfflineStatusTime)));
        cells.push(this.veriTableService.createVehicleRegCell(vehicle));
        cells.push({ ...this.veriTableService.createVehicleRegCell(vehicle.attachedTrailers[0]), hasNoData: vehicle.attachedTrailers.length == 0 });

        cells.push(this.veriTableService.createDriverCell(vehicle.Driver));
        cells.push(this.veriTableService.createLocationInformationCell(vehicle.PlotId, "row.data.Plot", vehicle.Plot));

        const availableChannels = this.cameraChannels?.filter(channel => channel.VehicleId === vehicle.Id && channel.IsActive);
        const channelCount: number = (availableChannels?.length) ? availableChannels.length : 0;

        if (!channelCount) {
            return;
        }

        cells.push({
            contents: `${channelCount}`,
            sortData: channelCount
        });

        const dmsEvents = this.videoEntries?.filter(event => event.VehicleId == vehicle.Id && this.videoEventCodeService.isDmsEvent(event.EventCode));

        cells.push({
            contents: `${dmsEvents.length}`,
            sortData: dmsEvents.length
        });

        const adasEvents = this.videoEntries?.filter(event => event.VehicleId == vehicle.Id && this.videoEventCodeService.isAdasEvent(event.EventCode));

        cells.push({
            contents: `${adasEvents.length}`,
            sortData: adasEvents.length
        });

        return cells;
    }

    private updateLatestOfflineStatusTime(vehicles: IVehicle[] = []): void {
        this.clearLatestOfflineStatusTime();

        vehicles.forEach(vehicle => {
            const onlineStatus = vehicle.onlineStatus;

            if (onlineStatus?.online || !onlineStatus?.lastOnlineTime) {
                return;
            }

            if (!this.latestOfflineStatusTime || this.dateService.date1IsAfterDate2(onlineStatus.lastOnlineTime, this.latestOfflineStatusTime)) {
                this.latestOfflineStatusTime = onlineStatus.lastOnlineTime;
                return;
            }
        });
    }

    private clearLatestOfflineStatusTime(): void {
        this.latestOfflineStatusTime = null;
    }
}

export class VehicleVideosPageComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
        };

        this.controller = ComponentController;
        this.template = require("./vehicleVideosPage.html");
    }
}
